import { 
  SignatureBlock, 
  SignatureDynamicData,
  ImageBlock,
  TextBlock,
  ContactRowBlock,
  SocialIconsBlock,
  DividerBlock,
  SpacerBlock,
} from '../blocks/types';
import { SignatureGridData, GridCell } from '../grid/types';
import { processDynamicFields } from '@/components/cost-center/document-defaults/dynamicFieldProcessor';

// Fixed container width for email compatibility
const EMAIL_CONTAINER_WIDTH = 500;

// Public Supabase storage base URL for email assets (icons)
// This URL is publicly accessible and works in all email clients
const SUPABASE_STORAGE_URL = 'https://ptdjtgonyxilanijzugt.supabase.co/storage/v1/object/public/email-assets';

// Options for HTML generation
export interface SignatureHtmlOptions {
  baseUrl?: string; // Legacy - no longer used for icons, kept for compatibility
}

// Get public Supabase storage URL for social icon PNG
const getSocialIconUrl = (platform: string): string => {
  const validPlatforms = ['facebook', 'instagram', 'linkedin', 'twitter', 'youtube', 'tiktok', 'whatsapp'];
  if (!validPlatforms.includes(platform)) return '';
  return `${SUPABASE_STORAGE_URL}/icons/social/${platform}.png`;
};

// Get public Supabase storage URL for contact icon PNG
const getContactIconUrl = (icon: string): string => {
  const validIcons = ['email', 'phone', 'whatsapp', 'website', 'location'];
  if (!validIcons.includes(icon)) return '';
  return `${SUPABASE_STORAGE_URL}/icons/contact/${icon}.png`;
};

const socialIconAlts: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  twitter: 'X',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  whatsapp: 'WhatsApp',
};

const processText = (text: string, data: SignatureDynamicData): string => {
  return processDynamicFields(text, {
    user: data.user,
    company: data.company,
  });
};

const renderImageBlock = (block: ImageBlock): string => {
  if (!block.src) return '';
  
  // Use HTML width/height attributes for email clients (they ignore CSS)
  const width = block.width || 100;
  const height = block.height || undefined;
  
  const styles = [
    `width: ${width}px`,
    height ? `height: ${height}px` : 'height: auto',
    block.borderRadius ? `border-radius: ${block.borderRadius}px` : '',
    'display: block',
    'max-width: 100%',
    'border: 0',
    'outline: none',
  ].filter(Boolean).join('; ');

  // Include both HTML attributes AND CSS for maximum compatibility
  const img = `<img src="${block.src}" alt="" width="${width}" ${height ? `height="${height}"` : ''} style="${styles}" />`;
  
  if (block.link) {
    return `<a href="${block.link}" target="_blank" style="text-decoration: none;">${img}</a>`;
  }
  return img;
};

const renderTextBlock = (block: TextBlock, data: SignatureDynamicData): string => {
  const processedContent = processText(block.content, data);
  if (!processedContent) return '';
  
  const styles = [
    `font-size: ${block.fontSize}px`,
    `font-weight: ${block.fontWeight}`,
    `color: ${block.color}`,
    `font-family: ${block.fontFamily}`,
    `text-align: ${block.textAlign || 'left'}`,
    'margin: 0',
    'padding: 0',
    'line-height: 1.4',
  ].join('; ');

  return `<p style="${styles}">${processedContent}</p>`;
};

const renderContactRowBlock = (block: ContactRowBlock, data: SignatureDynamicData): string => {
  const processedLabel = processText(block.label, data);
  if (!processedLabel) return '';
  
  // Use PNG icon with public Supabase URL for email compatibility
  const iconUrl = getContactIconUrl(block.icon);
  const iconHtml = iconUrl
    ? `<img src="${iconUrl}" alt="" width="${block.iconSize}" height="${block.iconSize}" style="display: inline-block; vertical-align: middle; margin-right: 6px; border: 0;" />`
    : '';

  let textContent = processedLabel;
  
  if (block.linkType === 'email') {
    textContent = `<a href="mailto:${processedLabel}" style="color: ${block.textColor}; text-decoration: none;">${processedLabel}</a>`;
  } else if (block.linkType === 'phone') {
    textContent = `<a href="tel:${processedLabel.replace(/\s/g, '')}" style="color: ${block.textColor}; text-decoration: none;">${processedLabel}</a>`;
  } else if (block.linkType === 'url') {
    const href = processedLabel.startsWith('http') ? processedLabel : `https://${processedLabel}`;
    textContent = `<a href="${href}" target="_blank" style="color: ${block.textColor}; text-decoration: none;">${processedLabel}</a>`;
  }

  const styles = [
    `font-size: ${block.fontSize}px`,
    `color: ${block.textColor}`,
    'margin: 0',
    'padding: 2px 0',
    'line-height: 1.6',
  ].join('; ');

  return `<p style="${styles}">${iconHtml}<span style="vertical-align: middle;">${textContent}</span></p>`;
};

const renderSocialIconsBlock = (block: SocialIconsBlock): string => {
  if (block.icons.length === 0) return '';
  
  // Use actual brand logo PNG images with public Supabase URLs
  const iconsHtml = block.icons.map(icon => {
    const iconUrl = getSocialIconUrl(icon.platform);
    const iconAlt = socialIconAlts[icon.platform] || icon.platform;
    if (!iconUrl) return '';
    
    const size = block.iconSize;
    
    // For circle style, wrap in a table cell with background for email compatibility
    if (block.iconStyle === 'circle') {
      const containerSize = size + 12;
      return `<a href="${icon.url}" target="_blank" style="display: inline-block; margin-right: ${block.spacing}px; text-decoration: none; vertical-align: middle;">
        <table cellpadding="0" cellspacing="0" border="0" style="border-collapse: collapse; display: inline-block;">
          <tr>
            <td width="${containerSize}" height="${containerSize}" style="background: #f0f0f0; border-radius: 50%; width: ${containerSize}px; height: ${containerSize}px; text-align: center; vertical-align: middle;">
              <img src="${iconUrl}" alt="${iconAlt}" width="${size}" height="${size}" style="display: block; margin: auto; border: 0;" />
            </td>
          </tr>
        </table>
      </a>`;
    }
    
    // Plain style - just the icon
    return `<a href="${icon.url}" target="_blank" style="display: inline-block; margin-right: ${block.spacing}px; text-decoration: none; vertical-align: middle;">
      <img src="${iconUrl}" alt="${iconAlt}" width="${size}" height="${size}" style="display: block; border: 0;" />
    </a>`;
  }).join('');

  return `<div style="margin: 4px 0;">${iconsHtml}</div>`;
};

const renderDividerBlock = (block: DividerBlock): string => {
  if (block.orientation === 'horizontal') {
    return `<table cellpadding="0" cellspacing="0" border="0" width="${block.length}%" style="border-collapse: collapse; margin: 8px 0;"><tr><td style="border-top: ${block.thickness}px solid ${block.color}; font-size: 1px; line-height: 1px;">&nbsp;</td></tr></table>`;
  }
  
  return `<div style="width: ${block.thickness}px; height: ${block.length}px; background: ${block.color};"></div>`;
};

const renderSpacerBlock = (block: SpacerBlock): string => {
  // Use table for consistent spacing in email clients
  return `<table cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse: collapse;"><tr><td style="height: ${block.height}px; font-size: 1px; line-height: 1px;">&nbsp;</td></tr></table>`;
};

const renderBlock = (block: SignatureBlock, data: SignatureDynamicData): string => {
  switch (block.type) {
    case 'image':
      return renderImageBlock(block);
    case 'text':
      return renderTextBlock(block, data);
    case 'contact':
      return renderContactRowBlock(block, data);
    case 'social':
      return renderSocialIconsBlock(block);
    case 'divider':
      return renderDividerBlock(block);
    case 'spacer':
      return renderSpacerBlock(block);
    default:
      return '';
  }
};

// Render a grid cell to HTML with fixed pixel widths for email
const renderGridCell = (cell: GridCell, data: SignatureDynamicData, containerWidth: number = EMAIL_CONTAINER_WIDTH): string => {
  // If cell has children (is split), render as nested table
  if (cell.children && cell.children.length > 0) {
    const isHorizontal = cell.splitDirection === 'horizontal';
    
    if (isHorizontal) {
      // Horizontal split = columns (use table with td cells with fixed pixel widths)
      const cells = cell.children.map((child, i) => {
        const widthPercent = cell.widths?.[i] || '50%';
        const percentValue = parseFloat(widthPercent.replace('%', '')) || 50;
        const pixelWidth = Math.round(containerWidth * (percentValue / 100));
        const content = renderGridCell(child, data, pixelWidth);
        return `<td width="${pixelWidth}" valign="top" style="vertical-align: top; width: ${pixelWidth}px; padding-right: 8px;">${content}</td>`;
      }).join('');
      
      return `<table cellpadding="0" cellspacing="0" border="0" width="${containerWidth}" style="border-collapse: collapse; width: ${containerWidth}px;"><tr>${cells}</tr></table>`;
    } else {
      // Vertical split = rows (stack in table rows)
      const rows = cell.children.map(child => {
        const content = renderGridCell(child, data, containerWidth);
        return `<tr><td style="padding: 0;">${content}</td></tr>`;
      }).join('');
      return `<table cellpadding="0" cellspacing="0" border="0" width="${containerWidth}" style="border-collapse: collapse; width: ${containerWidth}px;">${rows}</table>`;
    }
  }
  
  // If cell has content, render the block
  if (cell.content) {
    return renderBlock(cell.content, data);
  }
  
  // Empty cell
  return '';
};

// Convert grid data to HTML with email-compatible fixed-width tables
export const gridToHtml = (
  gridData: SignatureGridData,
  dynamicData: SignatureDynamicData,
  options: SignatureHtmlOptions = {}
): string => {
  // Note: baseUrl is no longer used - icons now use public Supabase storage URLs
  
  const cellsHtml = gridData.rootCells
    .map(cell => renderGridCell(cell, dynamicData, EMAIL_CONTAINER_WIDTH))
    .filter(Boolean)
    .join('');

  // Fixed-width outer table for email compatibility
  return `
    <table cellpadding="0" cellspacing="0" border="0" width="${EMAIL_CONTAINER_WIDTH}" style="border-collapse: collapse; width: ${EMAIL_CONTAINER_WIDTH}px; max-width: 100%; font-family: ${gridData.globalStyles.fontFamily};">
      <tr>
        <td style="padding: ${gridData.globalStyles.padding}px;">
          ${cellsHtml}
        </td>
      </tr>
    </table>
  `.trim();
};

export const gridDataToJson = (data: SignatureGridData): string => {
  return JSON.stringify(data);
};

export const jsonToGridData = (json: string): SignatureGridData | null => {
  try {
    const parsed = JSON.parse(json);
    if (parsed && parsed.version === 2 && Array.isArray(parsed.rootCells)) {
      return parsed as SignatureGridData;
    }
    return null;
  } catch {
    return null;
  }
};

// Legacy exports for backward compatibility
export const signatureToHtml = gridToHtml;
export const signatureDataToJson = gridDataToJson;
export const jsonToSignatureData = jsonToGridData;
